import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import draft1 from '../../assets/hero-demo/draft-1.png';
import draft2 from '../../assets/hero-demo/draft-2.png';
import draft3 from '../../assets/hero-demo/draft-3.png';

const CARD_LEFT = [40, 303, 566] as const;
const PILL_LEFT = [40, 196, 352] as const;
const GEN_IMG_BG = ['#3d1f2a', '#2a1f3d', '#3d2a1f'] as const;
const DRAFT_IMAGE_URLS = [draft1, draft2, draft3] as const;
const STAT_VALUES = ['1.2k', '84', '9.6k'] as const;
const STEP_DOTS = ['#ff6f91', '#ff9a6c', '#b892ff', '#ff6f91'] as const;

type DemoState = {
  stage: number;
  tone: number;
  topic: number;
  freq: number;
  approved: number;
  genRevealed: boolean;
  statsShown: boolean;
  cursorLeft: number;
  cursorTop: number;
  cursorVisible: boolean;
  ripple: boolean;
};

const INITIAL: DemoState = {
  stage: 0,
  tone: -1,
  topic: -1,
  freq: -1,
  approved: -1,
  genRevealed: false,
  statsShown: false,
  cursorLeft: 760,
  cursorTop: 50,
  cursorVisible: true,
  ripple: false,
};

export default function HeroProductDemo() {
  const { t } = useTranslation();
  const [demo, setDemo] = useState<DemoState>(INITIAL);
  const timeouts = useRef<number[]>([]);

  useEffect(() => {
    const clearAll = () => {
      timeouts.current.forEach((id) => window.clearTimeout(id));
      timeouts.current = [];
    };

    const dt = (offset: number, fn: () => void) => {
      timeouts.current.push(window.setTimeout(fn, offset));
    };

    const moveCursor = (left: number, top: number) => {
      setDemo((s) => ({ ...s, cursorLeft: left, cursorTop: top, cursorVisible: true }));
    };

    const click = (fn: () => void) => {
      setDemo((s) => ({ ...s, ripple: true }));
      fn();
      dt(320, () => setDemo((s) => ({ ...s, ripple: false })));
    };

    const scheduleCycle = () => {
      clearAll();
      setDemo({ ...INITIAL });

      dt(600, () => moveCursor(269, 113));
      dt(1150, () => click(() => setDemo((s) => ({ ...s, tone: 1 }))));
      dt(1750, () => moveCursor(425, 199));
      dt(2300, () => click(() => setDemo((s) => ({ ...s, topic: 2 }))));
      dt(2900, () => moveCursor(113, 285));
      dt(3450, () => click(() => setDemo((s) => ({ ...s, freq: 0 }))));
      dt(4300, () => setDemo((s) => ({ ...s, stage: 1, cursorVisible: false })));
      dt(5700, () => setDemo((s) => ({ ...s, genRevealed: true })));
      dt(7300, () =>
        setDemo((s) => ({
          ...s,
          stage: 2,
          cursorVisible: true,
          cursorLeft: 760,
          cursorTop: 50,
        }))
      );
      dt(7600, () => moveCursor(539, 118));
      dt(8150, () => click(() => setDemo((s) => ({ ...s, approved: 1 }))));
      dt(8950, () => setDemo((s) => ({ ...s, stage: 3, cursorVisible: false })));
      dt(9600, () => setDemo((s) => ({ ...s, statsShown: true })));
      dt(12200, () => scheduleCycle());
    };

    const start = window.setTimeout(() => scheduleCycle(), 500);
    timeouts.current.push(start);

    return clearAll;
  }, []);

  const groupLabels = t('home.demo.groupLabels', { returnObjects: true }) as string[];
  const tonePills = t('home.demo.tonePills', { returnObjects: true }) as string[];
  const topicPills = t('home.demo.topicPills', { returnObjects: true }) as string[];
  const freqPills = t('home.demo.freqPills', { returnObjects: true }) as string[];
  const statLabels = t('home.demo.statLabels', { returnObjects: true }) as string[];
  const stepTitles = t('home.demo.stepTitles', { returnObjects: true }) as string[];

  const pillGroups = [tonePills, topicPills, freqPills];
  const selectedIdx = [demo.tone, demo.topic, demo.freq];
  const draftCards = t('home.demo.draftCards', { returnObjects: true }) as Array<{
    title: string;
    subtitle: string;
  }>;

  return (
    <div className="mx-auto w-full max-w-[860px]">
      <div className="overflow-hidden rounded-[14px] border border-lc-borderStrong bg-lc-bg shadow-[0_50px_90px_-40px_#000]">
        <div className="flex items-center gap-2.5 border-b border-lc-border bg-lc-surface px-3.5 py-2">
          <div className="flex shrink-0 gap-[7px]">
            <span className="h-[11px] w-[11px] rounded-full bg-[#ff5f57]" />
            <span className="h-[11px] w-[11px] rounded-full bg-[#febc2e]" />
            <span className="h-[11px] w-[11px] rounded-full bg-[#28c840]" />
          </div>
          <div className="text-xs font-medium text-lc-faint">app.loopcast.io/dashboard</div>
        </div>

        <div className="relative h-[280px] select-none overflow-hidden bg-lc-bg sm:h-[400px]">
          <div
            className="pointer-events-none absolute left-0 top-0 origin-top-left scale-[0.48] sm:scale-[0.72] md:scale-100"
            style={{ width: 860, height: 400 }}
          >
            {/* STAGE 0: DEFINE */}
            <div
              className="pointer-events-none absolute inset-0 p-8 transition-opacity duration-[450ms] ease-in-out"
              style={{ opacity: demo.stage === 0 ? 1 : 0 }}
              aria-hidden={demo.stage !== 0}
            >
              <div className="mb-[22px] text-[14.5px] font-bold text-lc-text">{t('home.demo.defineTitle')}</div>
              {Array.isArray(groupLabels) &&
                groupLabels.map((label, i) => (
                  <div
                    key={label}
                    className="absolute left-10 text-[11.5px] font-semibold text-lc-faint"
                    style={{ top: 60 + i * 86 }}
                  >
                    {label}
                  </div>
                ))}
              {pillGroups.map((group, gi) =>
                (Array.isArray(group) ? group : []).map((label, pi) => {
                  const selected = selectedIdx[gi] === pi;
                  return (
                    <div
                      key={`${gi}-${label}`}
                      className="absolute flex h-[34px] w-[146px] items-center justify-center rounded-lg border text-[12.5px] font-semibold transition-all duration-300"
                      style={{
                        left: PILL_LEFT[pi],
                        top: 96 + gi * 86,
                        borderColor: selected ? '#ff6f91' : '#3d2e46',
                        background: selected ? '#3d1f2a' : '#251b2c',
                        color: selected ? '#ff9a6c' : '#c9b8d1',
                      }}
                    >
                      {label}
                    </div>
                  );
                })
              )}
            </div>

            {/* STAGE 1: GENERATING */}
            <div
              className="pointer-events-none absolute inset-0 p-8 transition-opacity duration-[450ms] ease-in-out"
              style={{ opacity: demo.stage === 1 ? 1 : 0 }}
              aria-hidden={demo.stage !== 1}
            >
              <div className="mb-2 text-[14.5px] font-bold text-lc-text">{t('home.demo.genTitle')}</div>
              <div className="mb-[22px] text-xs text-lc-faint">{t('home.demo.genDesc')}</div>
              {CARD_LEFT.map((left, i) => (
                <div
                  key={i}
                  className="absolute top-[104px] h-[250px] w-[250px] overflow-hidden rounded-xl border border-lc-borderStrong bg-lc-surface p-4"
                  style={{ left }}
                >
                  {demo.genRevealed ? (
                    <>
                      <div
                        className="mb-3 h-[110px] w-full overflow-hidden rounded-lg"
                        style={{ background: GEN_IMG_BG[i] }}
                      >
                        <img
                          src={DRAFT_IMAGE_URLS[i]}
                          alt={draftCards[i]?.title ?? 'Draft'}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="mb-2 text-[11.5px] font-semibold text-lc-faint">
                        {draftCards[i]?.title ?? ''}
                      </div>
                      <div className="text-[10.5px] text-lc-muted">{draftCards[i]?.subtitle ?? ''}</div>
                      <div className="mt-2 inline-block rounded-[5px] bg-[#2a1f3d] px-2.5 py-1 text-[10.5px] font-semibold text-lc-purple">
                        {t('home.demo.draftTag')}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-3 h-[110px] w-full animate-shimmerMove rounded-lg bg-[linear-gradient(100deg,#251b2c_30%,#332440_50%,#251b2c_70%)] bg-[length:200%_100%]" />
                      <div className="mb-2 h-[11px] w-[85%] animate-shimmerMove rounded bg-[linear-gradient(100deg,#251b2c_30%,#332440_50%,#251b2c_70%)] bg-[length:200%_100%]" />
                      <div className="h-[11px] w-[60%] animate-shimmerMove rounded bg-[linear-gradient(100deg,#251b2c_30%,#332440_50%,#251b2c_70%)] bg-[length:200%_100%]" />
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* STAGE 2: APPROVE */}
            <div
              className="pointer-events-none absolute inset-0 p-8 transition-opacity duration-[450ms] ease-in-out"
              style={{ opacity: demo.stage === 2 ? 1 : 0 }}
              aria-hidden={demo.stage !== 2}
            >
              <div className="mb-2 text-[14.5px] font-bold text-lc-text">{t('home.demo.approveTitle')}</div>
              <div className="mb-[22px] text-xs text-lc-faint">{t('home.demo.approveDesc')}</div>
              {CARD_LEFT.map((left, i) => {
                const approved = demo.approved === i;
                return (
                  <div
                    key={i}
                    className="absolute top-[104px] h-[250px] w-[250px] overflow-hidden rounded-xl bg-lc-surface p-4 transition-[border-color] duration-300"
                    style={{ left, border: `1.5px solid ${approved ? '#ff6f91' : '#3d2e46'}` }}
                  >
                    <div
                      className="mb-3 h-[110px] w-full overflow-hidden rounded-lg"
                      style={{ background: GEN_IMG_BG[i] }}
                    >
                      <img
                        src={DRAFT_IMAGE_URLS[i]}
                        alt={draftCards[i]?.title ?? 'Draft'}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="mb-2 text-[11.5px] font-semibold text-lc-faint">
                      {draftCards[i]?.title ?? ''}
                    </div>
                    <div className="text-[10.5px] text-lc-muted">{draftCards[i]?.subtitle ?? ''}</div>
                    <div
                      className="absolute right-3.5 top-3.5 flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-300"
                      style={{ background: approved ? '#ff6f91' : '#2b1f33' }}
                    >
                      {approved && <Check className="h-3.5 w-3.5 text-lc-ink" strokeWidth={3} />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* STAGE 3: PUBLISHED */}
            <div
              className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center p-8 transition-opacity duration-[450ms] ease-in-out"
              style={{ opacity: demo.stage === 3 ? 1 : 0 }}
              aria-hidden={demo.stage !== 3}
            >
              <div className="mb-[18px] flex h-16 w-16 items-center justify-center rounded-full bg-lc-pink">
                <Check className="h-[30px] w-[30px] text-lc-ink" strokeWidth={3} />
              </div>
              <div className="mb-6 text-[19px] font-extrabold text-lc-text">{t('home.demo.publishedTitle')}</div>
              <div
                className="flex gap-3 transition-opacity duration-500"
                style={{ opacity: demo.statsShown ? 1 : 0 }}
              >
                {Array.isArray(statLabels) &&
                  statLabels.map((label, i) => (
                    <div
                      key={label}
                      className="rounded-[9px] border border-lc-borderStrong bg-lc-surface px-[18px] py-2.5 text-center"
                    >
                      <div className="text-base font-extrabold text-lc-orange">{STAT_VALUES[i]}</div>
                      <div className="text-[10.5px] text-lc-faint">{label}</div>
                    </div>
                  ))}
              </div>
            </div>

            {/* FAKE CURSOR */}
            <div
              className="pointer-events-none absolute z-20"
              style={{
                left: demo.cursorLeft,
                top: demo.cursorTop,
                opacity: demo.cursorVisible ? 1 : 0,
                transition:
                  'left 550ms cubic-bezier(.4,0,.2,1), top 550ms cubic-bezier(.4,0,.2,1), opacity 300ms ease',
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="#f5eef7"
                stroke="#1c1420"
                strokeWidth="1"
                aria-hidden
              >
                <path d="M2 1l6 15 2-6 6-2z" />
              </svg>
              {demo.ripple && (
                <div className="absolute left-0.5 top-0.5 h-4 w-4 animate-rippleBurst rounded-full border-2 border-lc-pink" />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-[22px] flex flex-wrap justify-center gap-2.5">
        {Array.isArray(stepTitles) &&
          stepTitles.map((title, i) => {
            const on = i === demo.stage;
            return (
              <div
                key={title}
                className="flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all duration-300"
                style={{
                  borderColor: on ? '#ff6f91' : '#2b1f33',
                  background: on ? '#3d1f2a' : '#221733',
                  color: on ? '#f5eef7' : '#a8899f',
                }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: STEP_DOTS[i] }} />
                {title}
              </div>
            );
          })}
      </div>
    </div>
  );
}
